import { CircularProgress, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { ethers } from 'ethers';
import React, { useEffect, useRef, useState } from 'react';

import { useNFTContract } from '../../../../hooks/nft-contract.hooks';
import { useNFTErc721Contract } from '../../../../hooks/nft-erc-721-contract.hooks';
import type { Asset } from '../../../../lib/graph/MogulMarketplace';
import { AssetType } from '../../../../lib/graph/MogulMarketplace';
import { formatFloatField, parseFloatField } from '../../../../utils/form.util';
import { Button } from '../../../core/button';
import { InputField } from '../../../core/input-field';
import { Panel } from '../../panel/Panel.component';
import styles from './TransferNFTModal.module.css';

export interface TransferNFTModalProps {
  asset: Asset;
}
export interface TransferNFT extends TransferNFTModalProps {
  hideModal: () => void;
}

export interface TransferNFTFormValues {
  transferAmount?: number;
  toAddress?: string;
}

const INITIAL_VALUES: TransferNFTFormValues = {
  transferAmount: 1,
  toAddress: '',
};

const TransferNft: React.FC<TransferNFT> = ({
  asset,
  hideModal,
}: TransferNFT) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState(1);

  const nftContract = useNFTContract({
    nftContractAddress: asset.tokenAddress as string,
  });
  const nftErc721 = useNFTErc721Contract({
    nftContractAddress: asset.tokenAddress as string,
  });
  const [isErc721, setIsErc721] = useState(false);

  useEffect(() => {
    if (!nftContract.initialized || !nftErc721.initialized) {
      return;
    }

    void (async () => {
      let contract;
      if (asset.type !== AssetType.Erc721) {
        contract = nftContract;
      } else {
        contract = nftErc721;
        setIsErc721(true);
      }
      const rawBalance = await contract.balanceOf(asset.tokenId.toString());

      if (rawBalance) {
        setBalance(rawBalance.toNumber());
      }
    })();
  }, [nftContract.initialized, nftErc721.initialized]);

  const selection =
    useRef<{
      start: number | null;
      end: number | null;
      element: HTMLInputElement;
    } | null>(null);

  const input = <T extends keyof TransferNFTFormValues>(
    field: T,
    parse: (value: string) => string | undefined,
    format: (value: TransferNFTFormValues[T]) => string,
  ) => ({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      const value = target.value;
      const parsed = parse(value);

      selection.current = {
        element: target,
        start: target.selectionStart,
        end: target.selectionEnd,
      };

      if (field === 'toAddress') {
        setValues(v => ({ ...v, [field]: parsed }));
      }
      if (
        field === 'transferAmount' &&
        (!parsed || parseInt(parsed, 10) <= balance)
      ) {
        setValues(v => ({ ...v, [field]: parsed }));
      }
    },
    value: format(values[field]),
  });

  const handleClose = () => {
    if (submitting) {
      return;
    }

    hideModal();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();

    if (submitting || !nftContract.initialized || !nftErc721.initialized) {
      return;
    }

    const toAddress = values.toAddress === undefined ? '' : values.toAddress;
    const transferAmount =
      values.transferAmount === undefined ? '0' : values.transferAmount;

    if (!ethers.utils.isAddress(toAddress)) {
      return;
    }

    setSubmitting(true);

    try {
      if (asset.type !== AssetType.Erc721) {
        await nftContract.transferNft(
          nftContract.walletInfo.address ?? '',
          toAddress,
          asset.tokenId,
          transferAmount,
        );
      } else {
        await nftErc721.transferNft(
          nftContract.walletInfo.address ?? '',
          toAddress,
          asset.tokenId,
        );
      }
    } catch (error) {
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);

    handleClose();
  };

  return (
    <Panel bodyClassName={styles.panel + ' rounded-lg relative'}>
      <form
        autoComplete="off"
        onSubmit={handleSubmit}
        className={`${styles.transferNFTInfo} p-6`}
      >
        {!isErc721 && (
          <>
            <p className={styles.inputLabel}>Transfer Amount</p>
            <InputField
              label=""
              hideLabel
              InputProps={{
                disabled: submitting,
                className: 'styles.inputField',
                ...input('transferAmount', parseFloatField, formatFloatField),
              }}
            />
          </>
        )}
        <p className={styles.inputLabel}>To address</p>
        <InputField
          label=""
          hideLabel
          InputProps={{
            disabled: submitting,
            className: 'styles.inputField',
            ...input(
              'toAddress',
              value => value,
              value => (value === undefined ? '' : value),
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          disabled={
            !nftContract.initialized ||
            !nftErc721.initialized ||
            submitting ||
            !ethers.utils.isAddress(
              values.toAddress === undefined ? '' : values.toAddress,
            )
          }
        >
          {!submitting ? (
            'Transfer'
          ) : (
            <>
              Transferring
              <CircularProgress
                className={'placeholder-white::placeholder ml-4'}
                size={20}
              />
            </>
          )}
        </Button>
      </form>
      <IconButton
        className="h-10 w-10 top-2 right-2 z-30"
        style={{
          background: '#000000',
          border: 'none',
          position: 'absolute',
        }}
        onClick={hideModal}
      >
        <ClearIcon style={{ color: 'white' }} />
      </IconButton>
    </Panel>
  );
};
export const TransferNFTModal =
  (props: TransferNFTModalProps) =>
  ({ hideModal }: TransferNFT) => {
    return <TransferNft hideModal={hideModal} {...props} />;
  };
